import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { gbp } from "@/components/whatsapp/WaCostsTab";

const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

function Totals({ s }) {
  const cell = (conf, k) => s[conf][k];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="wa-attr-totals">
      {["confirmed", "probable"].map((conf) => (
        <div key={conf} className={`rounded-xl px-3 py-2 ${conf === "confirmed" ? "bg-emerald-50" : "bg-amber-50"}`} data-testid={`wa-attr-${conf}`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{conf}</div>
          <div className="text-sm text-slate-800"><b>{cell(conf, "enquiries")}</b> enquiries · <b>{cell(conf, "jobs")}</b> jobs · <b>{cell(conf, "sales")}</b> sales</div>
          <div className="text-xs text-slate-600">{gbp(cell(conf, "sales_value"))} sales inc VAT</div>
        </div>
      ))}
    </div>
  );
}

function Row({ a, onChanged }) {
  const [val, setVal] = useState(a.manual?.sale_value ?? "");
  const patch = async (b) => { try { await api.patch(`/wa/attributions/${a.id}`, b); onChanged(); } catch (e) { toast.error(err(e)); } };
  return (
    <li className={`py-2 border-t border-slate-100 ${a.removed ? "opacity-50" : ""}`} data-testid={`wa-attr-row-${a.job_legacy_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.confidence === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.confidence}</span>
        <a href={`/tasks/${a.job_legacy_id}`} className="font-medium text-indigo-600 hover:underline">{a.job_title || `Job ${a.job_legacy_id}`}</a>
        {a.is_enquiry && <span className="text-[10px] text-slate-500">enquiry</span>}
        {a.counts_as_sale && <span className="text-xs font-semibold text-slate-800" data-testid={`wa-attr-sale-${a.job_legacy_id}`}>Sale {gbp(a.sale_value_used)}{a.manual?.sale_value != null || a.manual?.is_sale != null ? " (manual)" : ""}</span>}
        <span className="ml-auto flex gap-1">
          <input type="number" min="0" step="0.01" value={val} placeholder="Sale £" onChange={(e) => setVal(e.target.value)} className="w-24 rounded-md border border-slate-200 px-2 h-7 text-xs" data-testid={`wa-attr-value-${a.job_legacy_id}`} />
          <Button size="sm" variant="outline" className="h-7" onClick={() => patch(val === "" ? { is_sale: false } : { is_sale: true, sale_value: Number(val) })} data-testid={`wa-attr-save-${a.job_legacy_id}`}>{val === "" ? "Not a sale" : "Set sale"}</Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => patch({ removed: !a.removed })} data-testid={`wa-attr-remove-${a.job_legacy_id}`}>{a.removed ? "Restore" : "Remove"}</Button>
        </span>
      </div>
      <ul className="mt-0.5 text-[11px] text-slate-500 list-disc pl-5" data-testid={`wa-attr-evidence-${a.job_legacy_id}`}>{(a.evidence || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
    </li>
  );
}

export function WaAttribution({ c, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/wa/campaigns/${c.id}/attribution`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const recalc = async () => { setBusy(true); try { const r = await api.post(`/wa/campaigns/${c.id}/attribution/recalculate`); toast.success(`${r.data.matched} matches found`); load(); onChanged(); } catch (e) { toast.error(err(e)); } finally { setBusy(false); } };
  const archive = async () => {
    if (!c.archived_at && !window.confirm("Archive this campaign? Clicks and replies after this point stop counting.")) return;
    try { await api.post(`/wa/campaigns/${c.id}/${c.archived_at ? "unarchive" : "archive"}`); onChanged(); } catch (e) { toast.error(err(e)); }
  };
  if (!d) return null;
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`wa-attr-panel-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold text-slate-700">Enquiries, jobs & sales from this campaign</div>
        <span className="text-[11px] text-slate-400" data-testid={`wa-attr-calculated-${c.id}`}>{d.summary.calculated_at ? `Last calculated ${new Date(d.summary.calculated_at).toLocaleString("en-GB")}` : "Not calculated yet"}</span>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={recalc} data-testid={`wa-attr-recalc-${c.id}`}>Recalculate</Button>
          <Button size="sm" variant="ghost" onClick={archive} data-testid={`wa-camp-archive-${c.id}`}>{c.archived_at ? "Unarchive" : "Archive campaign"}</Button>
        </span>
      </div>
      {c.archived_at && <div className="text-[11px] text-slate-500" data-testid={`wa-camp-archived-${c.id}`}>Archived {new Date(c.archived_at).toLocaleDateString("en-GB")} — only activity before this date counts.</div>}
      <Totals s={d.summary} />
      <ul data-testid={`wa-attr-list-${c.id}`}>{d.items.map((a) => <Row key={a.id} a={a} onChanged={() => { load(); onChanged(); }} />)}</ul>
      {!d.items.length && <p className="text-xs text-slate-400">No matches yet. Press Recalculate after people click or reply.</p>}
    </div>
  );
}
